import type { HelpArticleDef } from "./articleKit";
import {
  AppLink,
  ArticleLink,
  DeploymentNote,
  P,
  Sect,
  Steps,
  Tip,
  Ui,
} from "./articleKit";

export const SELLING_ARTICLES: HelpArticleDef[] = [
  {
    slug: "contacts-and-companies",
    title: "Contacts and companies",
    category: "Everyday selling",
    minutes: 5,
    summary:
      "The two core records, how they connect, and the timeline, tags, lifecycle stage, and lead score that live on them.",
    keywords: ["people", "records", "timeline", "notes", "tags", "lifecycle", "lead score"],
    related: ["deals-and-pipeline", "workflows-and-lead-scoring", "importing-your-data"],
    body: (
      <>
        <P>
          Everything in Obvious hangs off two records.{" "}
          <AppLink to="/contacts">Contacts</AppLink> are people;{" "}
          <AppLink to="/companies">Companies</AppLink> are the organizations
          they work for. A contact belongs to a company, a deal links to both,
          and every touch (form fill, email, note, task) lands on the
          contact's timeline so the full story reads in one place.
        </P>
        <Sect title="The contact page">
          <P>
            Open any contact and the center column is the timeline: notes,
            emails, form fills, and status changes, newest first. Add a note
            with the composer at the top, or a follow-up with{" "}
            <Ui>Add task</Ui>. The right rail holds the facts: company,
            title, email, phone, tags, background, and any{" "}
            <ArticleLink slug="settings-and-administration">
              custom fields
            </ArticleLink>{" "}
            your workspace has added.
          </P>
          <P>
            Two small badges do a lot of work. The <Ui>status</Ui> chip
            (cold, warm, hot, in contract) is your manual read on the
            relationship. The <Ui>lead score</Ui> badge is computed from
            behavior; hover it and it lists exactly which signals earned the
            points. How that works is covered in{" "}
            <ArticleLink slug="workflows-and-lead-scoring">
              Workflows and lead scoring
            </ArticleLink>
            .
          </P>
        </Sect>
        <Sect title="Lifecycle stages">
          <P>
            Every contact sits on a funnel ladder (Subscriber → Lead → MQL →
            SQL → Opportunity → Customer by default). Move people up as they
            qualify; the funnel chart on your reports reads directly from it.
            Admins rename or reorder the ladder at{" "}
            <AppLink to="/settings/lifecycle">
              Settings → Lifecycle stages
            </AppLink>
            .
          </P>
        </Sect>
        <Sect title="Finding your accounts">
          <P>
            Every contact has an account manager, and the filter rail on the{" "}
            <AppLink to="/contacts">Contacts page</AppLink> has a{" "}
            <Ui>Me</Ui> toggle under Account manager that cuts the list to
            just yours. Stack it with the activity filters (seen today, this
            week) to build your morning working list in two clicks. The deal
            board has the same owner filter across the top.
          </P>
        </Sect>
        <Sect title="Tags and organization">
          <P>
            Tags are freeform labels (industry, source, event, anything) that
            you filter and build{" "}
            <ArticleLink slug="lists-and-segments">lists</ArticleLink> on.
            They're shared across the workspace, so agree on a few and stay
            consistent - "tradeshow-2026" beats three spellings of the same
            idea.
          </P>
          <Tip>
            The fastest path to any record is <Ui>⌘K</Ui>. Type a few letters
            of a name and hit enter; you'll never scroll a list again.
          </Tip>
        </Sect>
      </>
    ),
  },
  {
    slug: "deals-and-pipeline",
    title: "Deals and the pipeline",
    category: "Everyday selling",
    minutes: 4,
    summary:
      "The kanban board, stages and win probabilities, close reasons, and how deal data feeds the forecast.",
    keywords: ["kanban", "board", "opportunities", "stages", "forecast", "won", "lost"],
    related: ["contacts-and-companies", "reports-and-attribution", "keep-an-eye-on"],
    body: (
      <>
        <P>
          <AppLink to="/deals">Deals</AppLink> is a kanban board: one column
          per stage, one card per opportunity. Drag a card to move it; the
          change is logged with a timestamp, which is what lets the reports
          measure velocity and spot deals going quiet. The owner filter at
          the top cuts the board to your deals alone.
        </P>
        <Sect title="Creating and working a deal">
          <Steps
            items={[
              <>
                Click <Ui>New deal</Ui> on the board (or from a company
                page). Name it, set the amount, link the company and the
                contacts involved.
              </>,
              <>
                Work it left to right. Notes and tasks added on the deal keep
                the record honest, and every stage change is tracked.
              </>,
              <>
                Close it as won or lost. Losing asks for a close reason
                (price, timing, competitor, and so on) - thirty seconds that
                make the loss-reasons report worth reading.
              </>,
            ]}
          />
        </Sect>
        <Sect title="Stages and the weighted forecast">
          <P>
            Each stage carries a win probability (Opportunity 20%, Proposal
            Sent 40%, In Negotiation 60%, and a Delayed parking stage at 10%
            by default). Your weighted pipeline
            on the{" "}
            <AppLink to="/reports/scorecard">Scorecard report</AppLink>{" "}
            multiplies each open deal by its stage probability, which is a far
            better forecast than raw pipeline totals. Tune stages and
            probabilities at{" "}
            <AppLink to="/settings/objects">Settings → Objects</AppLink>.
          </P>
        </Sect>
        <Sect title="Keeping deals from slipping">
          <P>
            A deal untouched for weeks is usually a deal dying quietly. The
            dashboard's{" "}
            <ArticleLink slug="keep-an-eye-on">Keep an eye on</ArticleLink>{" "}
            panel watches for exactly that and surfaces the longest-quiet open
            deals with their value, so the save happens while there's still
            time.
          </P>
        </Sect>
      </>
    ),
  },
  {
    slug: "tasks-and-calendar",
    title: "Tasks and the calendar",
    category: "Everyday selling",
    minutes: 3,
    summary:
      "Follow-ups that don't slip: the task list, the calendar view, and adding tasks from contacts, deals, or a calendar day.",
    keywords: ["todo", "follow up", "reminders", "due date", "schedule", "calendar"],
    related: ["keep-an-eye-on", "contacts-and-companies", "sequences"],
    body: (
      <>
        <P>
          Tasks are how Obvious keeps follow-through out of your head. Every
          task has a due date, a type (call, email, demo, and so on), and
          almost always a contact, so the context is one click away when the
          day comes.
        </P>
        <Sect title="Three ways to add one">
          <P>
            From a contact or deal page with <Ui>Add task</Ui> (best, because
            it's linked to the record). From the{" "}
            <AppLink to="/tasks">Tasks page</AppLink> with the button. Or
            straight on the calendar: click any day and the new task dialog
            opens with that date already set.
          </P>
        </Sect>
        <Sect title="Working the list">
          <P>
            The <AppLink to="/tasks">Tasks page</AppLink> shows your list and
            the month at a glance; overdue items surface on the{" "}
            <AppLink to="/">Dashboard</AppLink> in the Keep an eye on panel
            until you check them off. Done tasks stay on the contact's
            timeline as a record of the touch.
          </P>
          <Tip>
            Let automation set the routine ones.{" "}
            <ArticleLink slug="sequences">Sequences</ArticleLink> create call
            tasks mid-cadence, and{" "}
            <ArticleLink slug="workflows-and-lead-scoring">
              workflows
            </ArticleLink>{" "}
            can raise a task automatically when a contact goes quiet.
          </Tip>
        </Sect>
      </>
    ),
  },
  {
    slug: "inbox-and-email-capture",
    title: "Inbox and email capture",
    category: "Everyday selling",
    minutes: 4,
    summary:
      "How client email gets onto the timeline: the BCC address, full inbox sync, and the Inbox screen.",
    keywords: ["email", "bcc", "sync", "gmail", "outlook", "conversations", "capture"],
    related: ["contacts-and-companies", "integrations", "whats-live-vs-deployment"],
    body: (
      <>
        <P>
          A CRM is only as good as the email trail inside it. Obvious
          captures email two ways, in increasing order of effort and power.
        </P>
        <Sect title="Layer 1: the BCC address">
          <P>
            Your workspace gets a capture address. BCC it on any email you
            send and the message lands on the matching contact's timeline
            automatically, matched by their email address. No setup on the
            client's side, works from any mail app, day one.
          </P>
        </Sect>
        <Sect title="Layer 2: full inbox sync">
          <P>
            Connect your Google Workspace or Microsoft 365 mailbox on the{" "}
            <AppLink to="/settings/integrations">Integrations</AppLink> page
            and both directions sync: every client email in or out shows on
            the timeline, even ones sent from a phone at midnight, with no
            BCC to remember.
          </P>
        </Sect>
        <Sect title="The Inbox screen">
          <P>
            <AppLink to="/inbox">Inbox</AppLink> collects captured
            conversations in one place so you can triage without leaving
            Obvious: read the thread, reply, then jump to the contact when
            you want to log a note or raise a follow-up task.
          </P>
        </Sect>
        <DeploymentNote>
          The BCC address is issued as part of your deployment onboarding,
          and inbox sync connects when your workspace is deployed. The Inbox
          screen previews with sample conversations until then; see{" "}
          <ArticleLink slug="whats-live-vs-deployment">
            What's live now vs. at deployment
          </ArticleLink>
          .
        </DeploymentNote>
      </>
    ),
  },
];
